import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { createBrowserRouter } from 'react-router-dom';
import type { IWhiteLabelConfiguration } from '@shore/types';
import { UAParser } from 'ua-parser-js';

import './styles/index.css';
import Router from './Router';
import Tracer from './utils/tracer';
import Logger from './utils/logger';
import FeatureRollout from './utils/feature-rollout';
import createRouter from './deprecated-router';
import initI18next, { setupTranslations } from './initializers/i18next';
import initMomentJs from './initializers/moment';
import configureApiClient, { createApiClient } from './initializers/api-client';
import getGlobalValue from './utils/get-global-value';
import createStore from './store';
import * as defaultSubscriptions from './subscriptions';
// Not re-exported from ./subscriptions: everything there that is not
// destructured out below gets invoked with the store alone, and this one needs
// the merchant id.
import { observeAppointmentCreatedPush } from './subscriptions/appointment-created-push';
import loadApp from './app-manifest/app-loader';
import { fetchSubscription } from './utils/fetch-subscription';
import { fetchAppointmentUsage } from './utils/fetch-appointment-usage';
import { fetchCalendarV2OptIn } from './utils/fetch-calendar-v2-opt-in';
import { isCalendarV2OptInEligible } from './selectors';
import { fetchMerchantPaymentSettings } from './utils/fetch-merchant-payment-settings';
import { AppProviders } from './app/providers/AppProviders';
import {
  initializeSuccess,
  notificationSMSDisplaySeverity,
  setupBrowserLocaleAndErrorState,
} from './actions';
import { localStorage, sessionStorage } from './utils/namespaced-storage';
import { createAsyncScriptNode } from './utils/dom';
import createTrackMethod, {
  type AnalyticsInstance,
} from './initializers/track-method';
import createErrorLogger, {
  type SentryClient,
} from './initializers/error-logger';
import { createEventBus } from './initializers/event-bus';
import preLoadApp from './app-manifest/pre-loader';
import { values } from './utils/object';
import { SEVERITY, CELLO_PRODUCT_ID, API_ENV } from './constants';
import { WSB_MERCHANT_SOURCE } from './shared/constants/merchant';
import { applyGravityBrand } from './shared/styles/gravity-brand';
import { BETA_TOGGLES, isBetaToggleEnabled } from './features/beta/toggles';
import { document } from './env/browser';
import { keys as queryKeys } from './queries';
import { getUserRole } from './utils/user';
import type {
  AppShellApiClient,
  BookingConfigurations,
  CurrentMerchantData,
  GlobalsTools,
  SessionPermissionsPayload,
} from './shared/types';
import type { AppDispatch } from './store';

Tracer.init();
FeatureRollout.activate('api-consumption-analysis', { percentage: 30 });

// Populated below via Object.assign(apiClient, await configureApiClient(...))
// once the async bootstrap completes — configureApiClient's actual return
// shape is what makes AppShellApiClient accurate; this placeholder exists
// only so `deps`/`apiClient` have a stable reference to mutate into.
const apiClient = {} as AppShellApiClient;
const errorLogger = createErrorLogger(
  getGlobalValue('Sentry') as SentryClient | undefined
);
const router = createRouter();
const eventBus = createEventBus();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
const trackMethod = createTrackMethod(
  queryClient,
  () =>
    getGlobalValue('analytics') as
      | Partial<AnalyticsInstance>
      | null
      | undefined,
  errorLogger
);
const deps: GlobalsTools = {
  apiClient,
  trackMethod,
  router,
  sessionStorage,
  localStorage,
  errorLogger,
  eventBus,
  queryClient,
  // Real micro-apps use one of two contracts depending on which loading
  // mechanism they're consumed through: AppLoader (old) expects create() to
  // resolve to { destroy, dispatchAction? } (confirmed against
  // customers-app's real create()); MicroApp (new) expects { render }
  // (see MicroAppModule/shared/types). loadApp's own declared type only
  // models the latter, so bridging to GlobalsTools['loadApp'] (which models
  // the former) needs a cast here — a real duality, not a typing gap.
  loadApp: loadApp as GlobalsTools['loadApp'],
};

// createStore's param type carries an index signature (to accept the
// narrower per-effect dep shapes tests construct) that plain interfaces
// aren't assignable to without one of their own — intersect rather than
// touch that shared type, so this stays local to the one full-shape caller.
const store = createStore(deps as GlobalsTools & Record<string, unknown>);

const rootElement = document.getElementById('root') as HTMLElement;
const root = ReactDOM.createRoot(rootElement);

root.render(
  <AppProviders store={store} tools={deps}>
    <Router create={createBrowserRouter} />
  </AppProviders>
);

// Cello script should be loaded after `render` function from above.
// Otherwise, Cello gives an error "Could not find Shadow Root"
// for the payment screen in the widget
if (CELLO_PRODUCT_ID) {
  const celloScriptSrc =
    API_ENV === 'production'
      ? 'https://assets.cello.so/app/v2/latest/cello.js'
      : 'https://assets.sandbox.cello.so/app/v2/latest/cello.js';

  document.head.appendChild(
    createAsyncScriptNode({
      src: celloScriptSrc,
      type: 'module',
    })
  );
}

interface SmsUsageEvent {
  data: {
    attributes: {
      current_usage: string;
    };
  };
}

const smsDisplayNofification = (
  dispatch: AppDispatch,
  {
    data: {
      attributes: { current_usage },
    },
  }: SmsUsageEvent
): void => {
  switch (current_usage) {
    case '80%': {
      dispatch(notificationSMSDisplaySeverity(SEVERITY.WARNING));
      break;
    }
    case '100%': {
      dispatch(notificationSMSDisplaySeverity(SEVERITY.DANGER));
      break;
    }
    default: {
      dispatch(notificationSMSDisplaySeverity(SEVERITY.NONE));
    }
  }
};

(async function initialize() {
  try {
    Object.assign(
      apiClient,
      await configureApiClient(createApiClient, queryClient)
    );
    const {
      websocketListener,
      observeUnreadNotificationCount,
      observeAppointmentUsage,
      observeGravityCalendar,
      ...subscriptions
    } = defaultSubscriptions;

    values(subscriptions).forEach((fn) => fn(store));

    // Registered before initializeSuccess is dispatched, so the allowance
    // resolving at bootstrap is itself broadcast to any micro app already
    // mounted.
    observeAppointmentUsage(store, deps);
    observeGravityCalendar(store, deps);

    const myAccountData = await apiClient.getMyAccountData();
    // getCurrentMerchantData/getSessionPermissions/getBookingConfigs are
    // typed `| undefined` on AppShellApiClient because their backing
    // variables in initializers/api-client.ts's configureApiClient are only
    // conditionally assigned — but every path that fails to assign them
    // throws before configureApiClient returns (see that file), so by the
    // time this apiClient is live, they're always populated.
    const currentMerchantData =
      (await apiClient.getCurrentMerchantData()) as CurrentMerchantData;

    // Merchant known, and nothing `.gv-activated` has painted yet (PageLayout
    // is still on its no-merchantId spinner). Later than this and WSB merchants
    // flash the house brand.
    await applyGravityBrand(currentMerchantData.source);

    const whiteLabelConfig = await apiClient.getWhiteLabelConfig();
    const sessionPermissions =
      (await apiClient.getSessionPermissions()) as SessionPermissionsPayload;
    const bookingConfigs =
      (await apiClient.getBookingConfigs()) as BookingConfigurations;

    const subscription = await fetchSubscription(
      currentMerchantData.id,
      apiClient,
      queryClient
    );
    // Only merchants that can actually be capped: `onecom_wsb` with a positive
    // allowance on their subscription. Everyone else would get a 404.
    //
    // The subscription value decides only whether to ask. The allowance shown to
    // the merchant is always the one `/usage` returns, next to the count it is
    // rendered against.
    //
    // Also gated on the beta toggle while the upsell is being built, so the
    // request stays off in production until we choose to turn it on. Drop the
    // `isBetaToggleEnabled` term to ship it to every capped merchant.
    const isCappedMerchant =
      isBetaToggleEnabled(BETA_TOGGLES.bookingsUpgrade.key) &&
      currentMerchantData.source === WSB_MERCHANT_SOURCE &&
      Boolean(subscription?.limits?.monthly_appointments_creation_limit);

    // Never rejects — see the fetcher.
    const appointmentUsage = isCappedMerchant
      ? await fetchAppointmentUsage(
          currentMerchantData.id,
          apiClient,
          queryClient
        )
      : null;

    // Only users who can see the toggle have a choice worth reading; everyone
    // else stays on the calendar their merchant gets by default.
    const calendarV2OptIn =
      isCalendarV2OptInEligible(
        {
          source: currentMerchantData.source,
          createdAt: currentMerchantData.created_at,
          whiteLabel: currentMerchantData.organization?.whitelabel_slug,
        },
        Boolean(sessionPermissions.meta.calendar?.available)
      ) && (await fetchCalendarV2OptIn(apiClient, queryClient));

    const paymentSettings = await fetchMerchantPaymentSettings({
      merchantId: currentMerchantData.id,
      featureToggles: currentMerchantData.feature_toggles,
      apiClient,
      queryClient,
      errorLogger,
    });

    const organizationId = myAccountData.organization.id;

    const { browser, device } = UAParser();

    const sessionRole = getUserRole({
      merchantId: currentMerchantData.id,
      roles: myAccountData.roles,
    });

    if (!sessionRole) {
      errorLogger.log(new Error('Session role not found at initialization'), {
        merchantId: currentMerchantData.id,
        employee: JSON.stringify(myAccountData.employee),
        roles: JSON.stringify(myAccountData.roles),
      });
    }

    const whitelabel =
      currentMerchantData.organization?.whitelabel_slug || 'shore';

    queryClient.setQueryDefaults(queryKeys.trackingSessionMetadata, {
      gcTime: Infinity,
    });

    queryClient.setQueryData(queryKeys.trackingSessionMetadata, {
      whitelabel,
      has_subscription: !!subscription,
      subscription_status: subscription?.status,
      subscription_plan: subscription?.plan.id,
      subscription_since: subscription?.started_at,
      session_browser: browser.name,
      session_device_model: device.model,
      session_device_type: device.type,
      session_vendor: device.vendor,
      session_organization_id: organizationId,
      session_merchant_id: currentMerchantData.id,
      session_role: sessionRole,
      merchant_id: currentMerchantData.id,
    });

    const contracts = await apiClient.contractV2.findAll({
      filter: { organization: organizationId },
      include: ['packages', 'fees'],
    });

    const activeContract =
      contracts?.filter(
        (c) => c.state === 'accepted' || c.state === 'terminated'
      )[0] || null;

    const packages =
      activeContract?.packages.filter(
        (p) => p.category_code !== 'shore_app_marketplace'
      ) || null;

    const packageCode =
      Array.isArray(packages) && packages.length > 0
        ? packages[0].package_code
        : null;

    observeUnreadNotificationCount(store, deps, currentMerchantData.id);
    websocketListener(deps)(myAccountData.id)(
      'private-channel.sms_monthly_allowance.merchant_accounts'
    )('sms_monthly_allowance.v1.limit_reached')((d) => {
      smsDisplayNofification(store.dispatch, d as SmsUsageEvent);
    });

    // For Testing
    // const dummyData = { data: { attributes: { current_usage: '100%' } } };

    // setInterval(() => {
    //   smsDisplayNofification(store.dispatch, dummyData);
    // }, 15000);

    const { locale } = myAccountData;

    Tracer.setUser({
      merchantId: currentMerchantData.id,
      employeeId: myAccountData.employee.id,
    });

    Tracer.setGlobalContext('has_chargebee_subscription', !!subscription);

    Logger.init({
      merchantId: currentMerchantData.id,
      employeeId: myAccountData.employee.id,
    });

    await initMomentJs(locale);
    await initI18next(locale);
    await setupTranslations(locale);

    preLoadApp('search', locale, eventBus);
    preLoadApp('launchpad', locale, eventBus);

    store.dispatch(
      initializeSuccess(
        myAccountData,
        currentMerchantData,
        // getWhiteLabelConfig honestly returns Partial<...> (falls back to
        // {} on fetch failure — see initializers/api-client.ts), but
        // initializeSuccess's payload type wants the full shape; pre-existing
        // tension between those two already-typed files, not introduced here.
        whiteLabelConfig as IWhiteLabelConfiguration,
        packageCode,
        sessionPermissions,
        // fetchSubscription returns SubscriptionResponse | null,
        // initializeSuccess's payload wants | undefined — same "no
        // subscription" meaning either way.
        subscription ?? undefined,
        sessionRole,
        paymentSettings,
        bookingConfigs,
        appointmentUsage,
        calendarV2OptIn
      )
    );

    // After initializeSuccess, which assigns the bootstrap allowance
    // unconditionally and would overwrite anything refetched before it.
    //
    // Gated on the merchant being capped rather than on the read above having
    // succeeded: the backstops that recover a failed read live in here, so a
    // merchant who loads the shell while /usage is down would otherwise get no
    // upsell for the rest of the session even once it recovers. `isCappedMerchant`
    // is the set /usage serves, so nothing registered here re-reads in vain.
    if (isCappedMerchant) {
      observeAppointmentCreatedPush(
        store,
        { apiClient, queryClient },
        currentMerchantData.id,
        myAccountData.id
      );
    }
  } catch (error) {
    store.dispatch(setupBrowserLocaleAndErrorState(error));
  }
})();
